import { FileText, FileX, Mail, Star } from 'lucide-react';
import {
    Sheet,
    SheetClose,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from '@/components/ui/sheet';

import {
    Button,
    ButtonLink,
} from '@/components/ui/button';

import { useSession } from '@/hooks/use-session';
import { Link } from 'react-router-dom';

export function Header() {
    const { isAuthenticated } = useSession();

    const displayEmail = 'contact@helvedoc.com';

    return (
        <>
            {/* TOP BAR */}
            <div className="relative w-full overflow-hidden border-b border-white/[0.08] bg-[#b20303] text-white">
                <div className="relative mx-auto flex h-[40px] max-w-7xl items-center justify-between px-4 sm:px-6">

                    {/* Partie gauche */}
                    <div className="flex min-w-0 items-center">

                        {/* Entreprises */}
                        <div className="flex items-center gap-2">
                            <FileText
                                className="h-4 w-4 shrink-0 text-[#d8d8d8]"
                                strokeWidth={1.7}
                            />

                            <div className="flex items-baseline gap-1.5 whitespace-nowrap">
                                <span className="text-[13px] font-semibold tracking-[-0.01em] text-white">
                                    630 000+
                                </span>

                                <span className="hidden text-[12.5px] font-medium tracking-[-0.01em] text-white/75 sm:inline">
                                    entreprises suisses répertoriées
                                </span>
                            </div>
                        </div>

                        {/* Séparateur */}
                        <div className="mx-4 h-4 w-px bg-gradient-to-b from-transparent via-white/25 to-transparent sm:mx-6" />

                        {/* Avis */}
                        <div className="flex items-center gap-2 whitespace-nowrap">
                            <div
                                className="flex items-center gap-px"
                                aria-label="Note de 4,8 sur 5"
                            >
                                {[0, 1, 2, 3, 4].map((i) => (
                                    <Star
                                        key={i}
                                        className="h-[13px] w-[13px] fill-[#d7c2a2] text-[#d7c2a2]"
                                        strokeWidth={0}
                                    />
                                ))}
                            </div>

                            <span className="text-[12.5px] font-semibold tracking-[-0.01em] text-white">
                                Service noté par nos utilisateurs
                            </span>
                        </div>
                    </div>

                    {/* Email desktop */}
                    <a
                        href={`mailto:${displayEmail}`}
                        className="group ml-5 hidden items-center gap-2 whitespace-nowrap text-[12.5px] font-medium tracking-[-0.01em] text-white/80 transition-colors duration-300 hover:text-white md:flex"
                    >
                        <span className="grid h-6 w-6 place-items-center rounded-full border border-white/10 bg-white/[0.035] transition-all duration-300 group-hover:border-[#d7c2a2]/40 group-hover:bg-white/[0.07]">
                            <Mail
                                className="h-[12px] w-[12px] text-[#dedede] transition-colors duration-300 group-hover:text-[#d7c2a2]"
                                strokeWidth={1.7}
                            />
                        </span>

                        <span className="relative">
                            {displayEmail}

                            <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#d7c2a2] transition-all duration-300 group-hover:w-full" />
                        </span>
                    </a>
                </div>
            </div>

            {/* HEADER PRINCIPAL */}
            <header className="sticky top-0 z-40 w-full border-b border-[#ececec] bg-[linear-gradient(180deg,#ffffff_0%,#fcfcfc_100%)] shadow-[0_1px_0_rgba(255,255,255,0.9)]">

                <div className="mx-auto flex h-[74px] w-full max-w-[1536px] items-center justify-between gap-8 px-5 sm:px-8 lg:px-10 xl:px-14">

                    {/* Logo */}
                    <Link
                        to="/recherche"
                        aria-label="Accueil Helvedoc"
                        className="group flex shrink-0 items-center gap-3.5 select-none"
                    >
                        <span className="flex h-[52px] w-[52px] shrink-0 items-center justify-center">
                            <img
                                src="/logo.png"
                                alt="Logo HELVEDOC"
                                className="h-[50px] w-auto object-contain transition-transform duration-300 group-hover:scale-[1.02]"
                            />
                        </span>

                        <span className="hidden leading-none sm:block">

                            <span className="flex items-end whitespace-nowrap">

                                <span className="text-[25px] font-bold tracking-[-0.045em] text-[#111820]">
                                    HELVE
                                </span>

                                <span className="text-[25px] font-light tracking-[-0.045em] text-[#111820]">
                                    DOC
                                </span>

                                <span className="relative ml-1 pb-[2px] text-[15px] font-semibold tracking-[-0.03em] text-[#b20303]">
                                    .com

                                    <span className="absolute right-0 bottom-0 left-0 h-[1.5px] bg-[#b20303]" />
                                </span>

                            </span>

                            <span className="mt-1.5 flex w-full items-center justify-between whitespace-nowrap text-[7.5px] font-medium uppercase text-[#555a60]">

                                {Array.from(
                                    "Vos extraits d'entreprise en 2 clics",
                                ).map((character, index) => (
                                    <span key={index}>
                                        {character === ' '
                                            ? '\u00A0'
                                            : character}
                                    </span>
                                ))}

                            </span>

                        </span>
                    </Link>

                    {/* NAVIGATION DESKTOP */}
                    <nav className="hidden min-w-0 flex-1 items-center justify-center gap-8 lg:flex xl:gap-11">

                        <Link
                            to="/services"
                            className="group relative flex items-center gap-2 whitespace-nowrap px-1 py-3 text-[14px] font-medium tracking-[0.01em] text-[#2f3135] transition-colors duration-300 hover:text-[#c40000]"
                        >
                            Services

                            <span className="absolute bottom-[7px] left-1/2 h-px w-0 -translate-x-1/2 bg-[#c40000] transition-all duration-300 group-hover:w-full" />
                        </Link>

                        <Link
                            to="/entreprise"
                            className="group relative whitespace-nowrap px-1 py-3 text-[14px] font-medium tracking-[0.01em] text-[#2f3135] transition-colors duration-300 hover:text-[#c40000]"
                        >
                            Rechercher une entreprise

                            <span className="absolute bottom-[7px] left-1/2 h-px w-0 -translate-x-1/2 bg-[#c40000] transition-all duration-300 group-hover:w-full" />
                        </Link>

                        <Link
                            to="/contact"
                            className="group relative whitespace-nowrap px-1 py-3 text-[14px] font-medium tracking-[0.01em] text-[#2f3135] transition-colors duration-300 hover:text-[#c40000]"
                        >
                            Contact

                            <span className="absolute bottom-[7px] left-1/2 h-px w-0 -translate-x-1/2 bg-[#c40000] transition-all duration-300 group-hover:w-full" />
                        </Link>

                        <Link
                            to="/desinscription"
                            className="group relative whitespace-nowrap px-1 py-3 text-[14px] font-medium tracking-[0.01em] text-[#2f3135] transition-colors duration-300 hover:text-[#c40000]"
                        >
                            Résilier mon abonnement

                            <span className="absolute bottom-[7px] left-1/2 h-px w-0 -translate-x-1/2 bg-[#c40000] transition-all duration-300 group-hover:w-full" />
                        </Link>

                    </nav>

                    {/* ACTIONS DESKTOP */}
                    <div className="hidden shrink-0 lg:block">

                        {isAuthenticated ? (

                            <ButtonLink
                                to="/compte"
                                className="h-[48px] min-w-[156px] rounded-2xl bg-[#b20303] px-7 text-[15px] font-semibold text-white transition-all duration-300 hover:bg-[#950202]"
                            >
                                <span>Mon compte</span>

                                <svg
                                    viewBox="0 0 24 24"
                                    className="h-[18px] w-[18px]"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth={1.8}
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    aria-hidden="true"
                                >
                                    <path d="M20 21a8 8 0 0 0-16 0" />
                                    <circle cx="12" cy="7" r="4" />
                                </svg>
                            </ButtonLink>

                        ) : (

                            <ButtonLink
                                to="/connexion"
                                className="group relative h-[48px] min-w-[156px] overflow-hidden rounded-2xl border border-[#e3e3e3] bg-gradient-to-b from-[#f2f2f3] via-[#ececec] to-[#e5e5e5] px-7 text-[15px] font-semibold text-[#b20303] shadow-[0_10px_35px_rgba(15,23,42,0.08)] transition-all duration-300 hover:-translate-y-0.5"
                            >
                                <span className="relative z-10">
                                    Connexion
                                </span>

                                <svg
                                    viewBox="0 0 24 24"
                                    className="relative z-10 h-[18px] w-[18px]"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth={1.8}
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    aria-hidden="true"
                                >
                                    <path d="M20 21a8 8 0 0 0-16 0" />
                                    <circle cx="12" cy="7" r="4" />
                                </svg>
                            </ButtonLink>

                        )}

                    </div>

                    {/* MENU MOBILE */}
                    <Sheet>

                        <SheetTrigger asChild>
                            <Button
                                variant="outline"
                                size="icon"
                                className="h-10 w-10 shrink-0 rounded-xl border-[#dedede] bg-white text-[#1c1d20] shadow-[0_4px_14px_rgba(0,0,0,0.04)] transition-all duration-300 hover:border-[#c40000]/40 hover:bg-[#fff8f8] hover:text-[#c40000] lg:hidden"
                                aria-label="Ouvrir le menu"
                            >
                                <svg
                                    viewBox="0 0 24 24"
                                    className="h-[18px] w-[18px]"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth={1.9}
                                    strokeLinecap="round"
                                    aria-hidden="true"
                                >
                                    <path d="M4 7h16M4 12h16M4 17h16" />
                                </svg>
                            </Button>
                        </SheetTrigger>

                        <SheetContent
                            side="right"
                            className="flex w-[88vw] max-w-[390px] flex-col border-l border-[#ececec] bg-white p-0 text-[#171717] shadow-2xl"
                        >

                            {/* HEADER MOBILE */}
                            <div className="border-b border-[#ededed] bg-[linear-gradient(180deg,#ffffff_0%,#fafafa_100%)] px-5 py-5">

                                <SheetHeader>
                                    <SheetTitle className="text-left text-[16px] font-semibold tracking-[-0.01em] text-[#171717]">
                                        Menu
                                    </SheetTitle>
                                </SheetHeader>

                                {/* Mon compte */}
                                {isAuthenticated && (
                                    <div className="mt-4">

                                        <SheetClose asChild>
                                            <ButtonLink
                                                to="/compte"
                                                className="h-11 w-full rounded-xl bg-[#b20303] text-[13.5px] font-semibold text-white hover:bg-[#950202]"
                                            >
                                                <span>Mon compte</span>

                                                <svg
                                                    viewBox="0 0 24 24"
                                                    className="h-[17px] w-[17px]"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth={1.8}
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    aria-hidden="true"
                                                >
                                                    <path d="M20 21a8 8 0 0 0-16 0" />
                                                    <circle cx="12" cy="7" r="4" />
                                                </svg>
                                            </ButtonLink>
                                        </SheetClose>

                                    </div>
                                )}

                                {/* Email */}
                                <a
                                    href={`mailto:${displayEmail}`}
                                    className="mt-4 flex h-10 w-full items-center justify-start gap-2.5 rounded-xl border border-[#e4e4e4] bg-white px-4 text-[13px] font-medium text-[#333] transition-all hover:border-[#c40000]/30 hover:bg-[#fff8f8] hover:text-[#c40000]"
                                >
                                    <Mail
                                        className="h-4 w-4"
                                        strokeWidth={1.8}
                                    />

                                    <span>{displayEmail}</span>
                                </a>

                            </div>

                            {/* NAVIGATION MOBILE */}
                            <nav className="grid flex-1 content-start gap-2 px-4 py-4">

                                <SheetClose asChild>
                                    <Link
                                        to="/services"
                                        className="group flex min-h-11 items-center justify-between rounded-xl border border-[#eeeeee] bg-white px-4 py-3 text-[13.5px] font-medium text-[#2f3135] transition-all duration-300 hover:border-[#c40000]/25 hover:bg-[#fff9f9] hover:text-[#c40000]"
                                    >
                                        <span>Services</span>
                                        <MobileArrow />
                                    </Link>
                                </SheetClose>

                                <SheetClose asChild>
                                    <Link
                                        to="/entreprise"
                                        className="group flex min-h-11 items-center justify-between rounded-xl border border-[#eeeeee] bg-white px-4 py-3 text-[13.5px] font-medium text-[#2f3135] transition-all duration-300 hover:border-[#c40000]/25 hover:bg-[#fff9f9] hover:text-[#c40000]"
                                    >
                                        <span>
                                            Rechercher une entreprise
                                        </span>

                                        <MobileArrow />
                                    </Link>
                                </SheetClose>

                                <SheetClose asChild>
                                    <Link
                                        to="/a-propos"
                                        className="group flex min-h-11 items-center justify-between rounded-xl border border-[#eeeeee] bg-white px-4 py-3 text-[13.5px] font-medium text-[#2f3135] transition-all duration-300 hover:border-[#c40000]/25 hover:bg-[#fff9f9] hover:text-[#c40000]"
                                    >
                                        <span>À propos</span>
                                        <MobileArrow />
                                    </Link>
                                </SheetClose>

                                <SheetClose asChild>
                                    <Link
                                        to="/contact"
                                        className="group flex min-h-11 items-center justify-between rounded-xl border border-[#eeeeee] bg-white px-4 py-3 text-[13.5px] font-medium text-[#2f3135] transition-all duration-300 hover:border-[#c40000]/25 hover:bg-[#fff9f9] hover:text-[#c40000]"
                                    >
                                        <span>Contact</span>
                                        <MobileArrow />
                                    </Link>
                                </SheetClose>

                                <SheetClose asChild>
                                    <Link
                                        to="/desinscription"
                                        className="group flex min-h-11 items-center justify-between rounded-xl border border-[#efe4e4] bg-[#fdfafa] px-4 py-3 text-[13.5px] font-medium text-[#654343] transition-all duration-300 hover:border-[#c40000]/25 hover:bg-[#fff4f4] hover:text-[#c40000]"
                                    >
                                        <div className="flex items-center gap-2.5">

                                            <FileX
                                                className="h-4 w-4 opacity-65"
                                                strokeWidth={1.8}
                                            />

                                            <span>
                                                Résilier mon abonnement
                                            </span>

                                        </div>

                                        <MobileArrow />
                                    </Link>
                                </SheetClose>

                            </nav>

                            {/* CONNEXION MOBILE */}
                            {!isAuthenticated && (

                                <div className="sticky bottom-0 border-t border-[#ededed] bg-white/95 px-4 py-4 backdrop-blur-xl">

                                    <SheetClose asChild>
                                        <ButtonLink
                                            to="/connexion"
                                            className="h-11 w-full cursor-pointer rounded-xl border border-[#c40000] bg-[#c40000] text-[13.5px] font-semibold text-white shadow-[0_10px_28px_rgba(196,0,0,0.16)] transition-all duration-300 hover:bg-[#ad0000]"
                                        >
                                            <span>Connexion</span>

                                            <svg
                                                viewBox="0 0 24 24"
                                                className="h-[17px] w-[17px]"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth={1.8}
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                aria-hidden="true"
                                            >
                                                <path d="M20 21a8 8 0 0 0-16 0" />
                                                <circle
                                                    cx="12"
                                                    cy="7"
                                                    r="4"
                                                />
                                            </svg>
                                        </ButtonLink>
                                    </SheetClose>

                                </div>

                            )}

                        </SheetContent>
                    </Sheet>

                </div>
            </header>
        </>
    );
}

function MobileArrow() {
    return (
        <svg
            viewBox="0 0 20 20"
            className="h-4 w-4 opacity-55 transition-transform duration-300 group-hover:translate-x-0.5"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.8}
            strokeLinecap="round"
            aria-hidden="true"
        >
            <path d="M7.5 5 12.5 10l-5 5" />
        </svg>
    );
}